import { useCallback, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { SelectedCompany } from '@/components/company-select-bh/company-select-bh';
import { useCompaniesList } from '@/hooks/use-company/use-company';
import { useSelectedCompany } from '@/hooks/use-selected-company/use-selected-company';
import { CompaniesResponse } from '@/services/company-service/company-service.types';
import { useCompanyStore } from '@/store/app.store';

export type OutboundCompany = NonNullable<SelectedCompany>;

export type OutboundContext = { company: OutboundCompany };

/** Only these can be worked on here: the CRM refuses every call for the others. */
export const callsOut = (company: CompaniesResponse[number]) => company.outbound_calls_enabled;

/** The company every tab works on, chosen once in the section shell. */
export const useOutboundContext = () => useOutletContext<OutboundContext>();

/** Follows the dashboard-wide company switcher, as the settings tabs do. */
export function useOutboundCompanySelection() {
  const { selectedCompany, setSelectedCompany } = useSelectedCompany({
    storageKey: 'selectedCompany_outbound',
  });
  const { companyName, setCompanyName } = useCompanyStore();
  const { companies } = useCompaniesList();

  useEffect(() => {
    const company = companyName
      ? companies?.find((c) => c.name === companyName && callsOut(c))
      : undefined;
    if (!company) return;
    setSelectedCompany((prev) =>
      prev?.id === company.id
        ? prev
        : { id: company.id, name: company.name, time_zone: company.time_zone }
    );
  }, [companyName, companies, setSelectedCompany]);

  const selectCompany = useCallback(
    (company: SelectedCompany) => {
      setSelectedCompany(company);
      setCompanyName(company?.name);
    },
    [setSelectedCompany, setCompanyName]
  );

  // A stored pick outlives the company's outgoing number.
  const usable = companies?.some((c) => c.id === selectedCompany?.id && callsOut(c));

  return { selectedCompany: usable ? selectedCompany : undefined, selectCompany };
}
